import { observable, set } from 'mobx';
import { pascalCaseData, toNumber } from '~utils/CaseUtils';
import { formatLoyaltyLevel } from '~utils/FormatterUtils';
import { IRewardsInfo } from '~views/dashboard/loyalty-rewards/ILoyaltyRewards';
import {
    ILoyaltyRewardsResponse,
    IOfferDetailsResponse
} from '~api/interfaces/ILoyaltyRewardsResponse';
import { getSortedOffersInfo, PriorityOrderSort, CategoryOrderSort } from '~utils/OffersSorting';
import { INVALID_INVENTORY_STATUSES } from '~internal/utils/OfferNotificationContext';

class Rewards {
    @observable public rewardsDataFormatted: IRewardsInfo[] = [];
    @observable public totalRedeemedPoints: number = 0;

    static fromJSON(rewardsData: ILoyaltyRewardsResponse): Rewards {
        const rewardsOfferInfo = new Rewards();
        const { user: {benefits: offerData}, currentLoyaltyLevel } = rewardsData;

        const rewardsOfferFields = offerData || [];

        const totalRedeemedPoints = rewardsOfferFields?.reduce(
            (redeemedPoints: number, offerDetails) => {
                const inCartStatus =
                    offerDetails?.skuBased ? offerDetails?.sku?.inCart : 0;

                return inCartStatus
                    ? redeemedPoints + toNumber(offerDetails?.points)
                    : redeemedPoints;
            },
            0
        ) as number;

        const filteredOfferFields = rewardsOfferFields?.filter((levelDetails) =>
            !levelDetails?.skuBased
                ? formatLoyaltyLevel(levelDetails?.criteria as string)?.includes(
                      currentLoyaltyLevel
                  )
                : levelDetails
        );

        const rewardsDataFormatted = filteredOfferFields?.map(
            (offerDetails: IOfferDetailsResponse) => {
                const isChoiceOfSku = Rewards.isChoiceOfSku(offerDetails);
                Rewards.fillSkuData(offerDetails);

                return {
                    inCart:
                        isChoiceOfSku
                            ? offerDetails?.sku?.inCart
                            : offerDetails?.inCart,
                    inventoryStatus: Rewards.getEvaluatedInventoryStatus(offerDetails, true),
                    meetsCriteria: offerDetails?.meetsCriteria,
                    offerButtonLabel: pascalCaseData(offerDetails?.promo?.text?.action as string, true),
                    redeemedStatus: offerDetails?.promo?.text?.redeemed,
                    offerCode: offerDetails?.offerCode,
                    offerDescription: offerDetails?.promo?.text?.description,
                    offerHowToUse: offerDetails?.promo?.text?.howToUse,
                    offerImage:
                        offerDetails?.skuBased && offerDetails.sku
                            ? offerDetails?.sku?.largeImage
                            : offerDetails?.image,
                    offerLinkAction: offerDetails?.promo?.URL?.action,
                    offerPoints: offerDetails?.points,
                    offerTitle: offerDetails?.promo?.text?.title,
                    offerUsed: offerDetails?.used,
                    offerView: isChoiceOfSku ? 'ChoicesOfSku' : (offerDetails?.skuBased && offerDetails.sku ? 'Sku' : ''),
                    productName: offerDetails?.sku?.productName,
                    altProductImages: offerDetails?.sku?.altProductImages || [],
                    productWhoItIsFor: offerDetails?.sku?.productWhoItIsFor,
                    productConcern: offerDetails?.sku?.productConcern,
                    shortDescription: offerDetails?.sku?.shortDescription,
                    shadeName: offerDetails?.sku?.shadeName,
                    productSize: offerDetails?.sku?.PRODUCT_SIZE,
                    skuID: offerDetails?.sku?.skuBaseID?.replace('SKU', ''),
                    path: offerDetails?.sku?.path,
                    pointsNeeded: offerDetails?.pointsNeeded || 0,
                    benefits: JSON.parse(offerDetails?.benefits || '[]'),
                    highestPriorityClass: null,
                    criteriaViewValidations: offerDetails?.criteriaViewValidations,
                    criteria: JSON.parse(offerDetails?.criteria || '[]'),
                    offerNotificationFields: {
                        criteriaFieldsList: offerDetails?.criteriaFields,
                        criteriaAllowedTierList: offerDetails?.criteriaAllowedTiers,
                        criteriaRequiredSkuList: offerDetails?.criteriaRequiredSkus,
                        inventoryStatus: Rewards.getEvaluatedInventoryStatus(offerDetails, false),
                        pointsNeeded: offerDetails?.pointsNeeded
                    },
                    isChoiceOfSku
                };
            }
        );

        const modifiedData = {
            rewardsDataFormatted,
            totalRedeemedPoints: totalRedeemedPoints
        };

        set(rewardsOfferInfo, modifiedData);

        return rewardsOfferInfo;
    }

    private static isChoiceOfSku(offerDetails: IOfferDetailsResponse) {
        return offerDetails?.skuBased && offerDetails?.sku ? true : false;
    }

    private static fillSkuData(offerDetails: IOfferDetailsResponse) {
        if (!offerDetails?.sku && offerDetails?.skus?.choices?.length === 1) {
            const productCodeChoices = offerDetails.skus.choices[0].split(' ');
            let primaryChoice = null;
            let alternateChoice = null;
            let validPrimaryChoice = false;
            let validAlternateChoice = false;
            if (productCodeChoices.length > 0) {
                const primarySku = productCodeChoices.shift();
                primaryChoice = offerDetails.skus.choicesPcatData.find(choicePcatData => (choicePcatData.skuData.productCode === primarySku || choicePcatData.skuData.skuID === primarySku || choicePcatData.skuData.skuBaseID === primarySku));
                validPrimaryChoice = !INVALID_INVENTORY_STATUSES.includes(primaryChoice?.skuData.inventoryStatus || '') && primaryChoice !== undefined;

                if (!validPrimaryChoice) {
                    validAlternateChoice = productCodeChoices.some((sku) => {
                        alternateChoice = offerDetails?.skus?.choicesPcatData?.find(opt => {
                            const skuInfo = opt?.skuData ?? {};

                            return skuInfo?.productCode === sku || skuInfo?.skuID === sku || skuInfo?.skuBaseID === sku;
                        });

                        return (
                            alternateChoice &&
                            !INVALID_INVENTORY_STATUSES.includes(alternateChoice?.skuData?.inventoryStatus || '')
                        );
                    });
                }
            }

            const choice = validPrimaryChoice
                ? primaryChoice
                : (validAlternateChoice ? alternateChoice : primaryChoice || null);

            if (choice) {
                offerDetails.sku = choice.skuData;
            }
        }
    }

    private static getEvaluatedInventoryStatus(offerDetails: IOfferDetailsResponse, toLowerCase: boolean) {
        if (offerDetails?.skuBased) {
            if (offerDetails.sku) {
                return toLowerCase ? offerDetails.sku.inventoryStatus?.toLowerCase() : offerDetails.sku.inventoryStatus;
            } else {
                if (offerDetails.skus?.choicesPcatData?.length > 0) {
                    let validInventoryStatus: string | null = null;
                    let invalidInventoryStatus: string | null = null;
                    let invalidInventoryStatusCounter = 0;
                    offerDetails.skus.choicesPcatData.forEach(sku => {
                        if (sku.skuData?.inventoryStatus) {
                            if (INVALID_INVENTORY_STATUSES.includes(sku.skuData.inventoryStatus)) {
                                invalidInventoryStatus = toLowerCase ? sku.skuData.inventoryStatus.toLowerCase() : sku.skuData.inventoryStatus;
                                invalidInventoryStatusCounter++;
                            } else {
                                validInventoryStatus = toLowerCase ? sku.skuData.inventoryStatus.toLowerCase() : sku.skuData.inventoryStatus;
                            }
                        }
                    });

                    return offerDetails.skus.choicesPcatData.length > invalidInventoryStatusCounter ? validInventoryStatus : invalidInventoryStatus;
                } else {
                    return null;
                }
            }
        } else {
            return null;
        }
    }

    public static getSortedRewardsInfo(rewardsDataFormatted: IRewardsInfo[], totalRedeemedPoints: number,
        priorityOrder?: PriorityOrderSort[], categoryOrder?: CategoryOrderSort[]) {
        const rewardsOfferInfo = new Rewards();
        const modifiedData = {
            rewardsDataFormatted: getSortedOffersInfo(rewardsDataFormatted, priorityOrder, categoryOrder),
            totalRedeemedPoints
        };
        set(rewardsOfferInfo, modifiedData);

        return rewardsOfferInfo;
    }
}

export default Rewards;
